import {
  Dialog,
  Flex,
  Grid,
  IconButton,
  ReadOnlyProperty,
  Text,
  toaster,
  Tooltip,
} from 'components'
import { GiftIcon } from 'components/icons'
import { startCase } from 'lodash-es'
import { useRef, useState } from 'react'
import { Lead } from 'types'
import { getLeads } from 'utils'
import { makeTypeByField } from 'utils/tableUtils'
import { useEvent } from '../../utils/eventUtils'
import LeadTempIcon from './LeadTempIcon'

export default function GiveawayButton() {
  const { event } = useEvent()
  const [open, setOpen] = useState(false)
  const [randomLead, setRandomLead] = useState<Lead | null>(null)
  const [loading, setLoading] = useState(false)

  const viewedLeadOffsets = useRef<Set<number>>(new Set())

  async function handleSelect() {
    if (!event?.id || !event?.workspaceId || !event?.leadCount) {
      setRandomLead(null)
      return
    }

    if (viewedLeadOffsets.current.size >= event.leadCount) {
      toaster.create({
        type: 'error',
        description: 'No more leads available to display.',
      })
      return
    }

    try {
      setOpen(true)
      setLoading(true)
      let randomOffset = 0
      do {
        randomOffset = Math.floor(Math.random() * event.leadCount)
      } while (viewedLeadOffsets.current.has(randomOffset))
      viewedLeadOffsets.current.add(randomOffset)

      const response = await getLeads({
        eventId: event.id,
        workspaceId: event.workspaceId,
        offset: randomOffset,
        limit: 1,
      })

      const leads = response.data
      if (leads && leads.length > 0) {
        setRandomLead(leads[0])
      } else {
        setRandomLead(null)
      }
    } catch (error) {
      console.error('Error fetching random lead for giveaway:', error)
      setRandomLead(null)
    } finally {
      setLoading(false)
    }
  }

  function handleOpenChange({ open }: { open: boolean }) {
    if (open) handleSelect()
    else setOpen(false)
  }

  return (
    <>
      <Tooltip content="Giveaway">
        <IconButton
          variant="solid"
          icon={<GiftIcon type="outline" />}
          onClick={() => handleOpenChange({ open: true })}
          borderRadius="sm"
          aria-label="Giveaway"
        />
      </Tooltip>
      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
        title="Giveaway"
        contentProps={{ maxW: '700px', maxH: '85vh' }}
        bodyProps={{ p: 4, overflow: 'visible' }}
      >
        {loading ? (
          <Text textStyle="bodyMedium" opacity={0.7} p={4}>
            Loading...
          </Text>
        ) : randomLead ? (
          <GiveawayLeadContent lead={randomLead} />
        ) : (
          <Text textStyle="bodyMedium" opacity={0.7} p={4}>
            No leads available to display.
          </Text>
        )}
      </Dialog>
    </>
  )
}

function GiveawayLeadContent({ lead }: { lead: Lead }) {
  const leadProperties: Array<{ key: string; value: any }> = []

  const primaryKeys: (keyof Lead)[] = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'company',
    'title',
    'school',
    'address',
    'address2',
    'city',
    'state',
    'zip',
    'country',
  ]

  primaryKeys.forEach(key => {
    leadProperties.push({ key, value: lead[key] || '' })
  })

  leadProperties.push({ key: 'temp', value: lead.temp })

  leadProperties.push({ key: 'notes', value: lead.notes || '' })
  leadProperties.push({ key: 'badge', value: lead.badge || '' })
  if (lead.name !== undefined) {
    leadProperties.push({ key: 'name', value: lead.name || '' })
  }
  if (lead.followUp !== undefined) {
    leadProperties.push({ key: 'followUp', value: lead.followUp ? 'Yes' : 'No' })
  }
  if (lead.optIn !== undefined) {
    leadProperties.push({ key: 'optIn', value: lead.optIn ? 'Yes' : 'No' })
  }

  if (lead.custom) {
    Object.entries(lead.custom).forEach(([key, value]) => {
      leadProperties.push({ key, value: value || '' })
    })
  }

  if (lead.smartProperties && lead.smartProperties.length > 0) {
    lead.smartProperties.forEach(prop => {
      leadProperties.push({
        key: prop.name,
        value: prop.text || prop.number || 'No Match Found',
      })
    })
  }

  const fieldTypes = makeTypeByField([lead])

  async function handleCopyEmail(email: string) {
    try {
      await navigator.clipboard.writeText(email)
      toaster.create({
        type: 'success',
        description: 'Email copied to clipboard',
      })
    } catch (error) {
      console.error('Failed to copy email:', error)
      toaster.create({
        type: 'error',
        description: 'Failed to copy email',
      })
    }
  }

  return (
    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
      {leadProperties.map(({ key, value }) => {
        if (key === 'temp') {
          return (
            <ReadOnlyProperty
              key={key}
              label={startCase(key)}
              value={
                <Flex align="center" gap={2}>
                  <LeadTempIcon value={value} />
                  <Text textStyle="bodySmall" fontWeight="500">
                    {value ? startCase(value) : 'Unset'}
                  </Text>
                </Flex>
              }
            />
          )
        }

        if (key === 'email' && value && value !== '\u00A0') {
          const emailValue = value.toString()
          return (
            <ReadOnlyProperty
              key={key}
              label={startCase(key)}
              value={emailValue}
              dataType={fieldTypes[key]}
              textProps={{
                minH: '32px',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                _hover: { opacity: 0.8 },
                onClick: () => handleCopyEmail(emailValue),
              }}
            />
          )
        }

        const displayValue =
          value === null || value === undefined || value === ''
            ? '\u00A0' // Non-breaking space to maintain consistent height
            : value.toString()

        return (
          <ReadOnlyProperty
            key={key}
            label={startCase(key)}
            value={displayValue}
            dataType={fieldTypes[key]}
            textProps={{ minH: '32px', display: 'flex', alignItems: 'center' }}
          />
        )
      })}
    </Grid>
  )
}
